/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getRouteStaticData,
  markFormStateAsTouched,
  PlainImmutablePageState,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import {
  cleanScheduleTaskRangePredecessors,
  getScheduleTaskId,
  getScheduleTaskSubtreeRange,
  propagateExcludeTasksFromGanttChart,
  recalculateScheduleTasks,
  scheduleTaskArray,
  setScheduleTaskFields,
} from '@gh/builder-shared-gantt-chart';

import { last, map, merge, sortBy } from 'lodash';
import { Subject } from 'rxjs';

import {
  ScheduleTemplateDetails,
  ScheduleTemplateDetailsInfo,
  scheduleTemplateDetailsInit,
  ScheduleTemplateTask,
  START_TASK_NUMBER,
  validateScheduleTemplateInfo,
} from '../../resources/scheduling-schedule-template.model';

@Injectable()
export class SchedulingScheduleTemplateDetailsFormState extends PlainImmutablePageState<ScheduleTemplateDetails> {

  recalculate$$ = new Subject<void>();

  propagatedTasks: number[] = [];

  tasks$ = this.select(({ tasks }) => tasks);
  info$ = this.select(({ info }) => info);

  maxNumber: number;

  constructor(private userContext: UserContext,
              private route: ActivatedRoute) {
    super(scheduleTemplateDetailsInit(getRouteStaticData(route)['type']), FormMode.CreateMode);
  }

  get templateId(): Maybe<ID> {
    return this.value.templateId;
  }

  get isTopFormInvalid(): boolean {
    return isNotNil(validateScheduleTemplateInfo(getFormStateSnapshotValue(this.value.info)));
  }

  get info(): FormStateSnapshot<ScheduleTemplateDetailsInfo> {
    return this.value.info;
  }

  build(scheduleTemplateDetails: ScheduleTemplateDetails,
        formMode: FormMode,
        withResetDirty: boolean = true): void {
    this.setMode(formMode);

    this.setValue(scheduleTemplateDetails);
    this.initMaxNumber();
    if (withResetDirty) {
      this.resetDirty();
    }
  }

  highlightInfoSnapshot(): void {
    this.setField('info', markFormStateAsTouched(this.value.info));
  }

  updateInfoSnapshot(info: FormStateSnapshot<ScheduleTemplateDetailsInfo>): void {
    this.setField('info', markFormStateAsTouched(info));
    this.setDirty(true);
  }

  updateTasks(tasks: ScheduleTemplateTask[]): void {
    this.startChangeDetection();
    this.setField('tasks', tasks);
    this.setDirtyIfChanged();
  }

  addTask(taskListItem: ScheduleTemplateTask): void {
    this.updateTasks(<ScheduleTemplateTask[]>scheduleTaskArray.append(this.value.tasks, merge(taskListItem, {
      taskNumber: ++this.maxNumber,
    })));
  }

  setTaskField(task: ScheduleTemplateTask, field: any, value: any): void {
    const taskId = getScheduleTaskId(task);

    this.startChangeDetection();
    this.setField(
      'tasks',
      <ScheduleTemplateTask[]>scheduleTaskArray.update(
        this.value.tasks,
        taskId,
        (targetTask) => setScheduleTaskFields(targetTask, { [field]: value })));
    if (field === 'isExcludeFromGanttChart') {
      this.setField(
        'tasks',
        <ScheduleTemplateTask[]>propagateExcludeTasksFromGanttChart(this.value.tasks, taskId, value));
    }
    if (this.changed) {
      // when task is changed => duration of its parent task should be actualized
      this.setField('tasks', <ScheduleTemplateTask[]>recalculateScheduleTasks(this.value.tasks, true));
    }
    this.setDirtyIfChanged();

    if (field === 'isExcludeFromGanttChart') {
      this.recalculate$$.next();
    }
  }

  deleteTask(task: ScheduleTemplateTask): void {
    // get range of tasks including task to be deleted and all its descendants
    const range = getScheduleTaskSubtreeRange(this.value.tasks, getScheduleTaskId(task));

    if (range) {
      // remove all predecessors for range of deleted tasks
      const tasksWithCleanedPredecessors = cleanScheduleTaskRangePredecessors(this.value.tasks, range);
      // remove all tasks from the range
      this.setField(
        'tasks',
        <ScheduleTemplateTask[]>tasksWithCleanedPredecessors
          .slice(0, range.from)
          .concat(tasksWithCleanedPredecessors.slice(range.to)));
    }
    this.setField('tasks', <ScheduleTemplateTask[]>recalculateScheduleTasks(this.value.tasks, true));
    this.setDirty();

    this.recalculate$$.next();
  }

  findTemplateTaskByTaskNumber(taskNumber: number): ScheduleTemplateTask {
    return <ScheduleTemplateTask>scheduleTaskArray.findBy(
      this.value.tasks,
      (task: ScheduleTemplateTask) => task.taskNumber === taskNumber);
  }

  private initMaxNumber(): void {
    this.maxNumber = last(sortBy(map(
      this.project(({ tasks }: ScheduleTemplateDetails) => tasks),
      ({ taskNumber }) => taskNumber,
    ))) || START_TASK_NUMBER;
  }
}
